import * as T from 'three';
import type { SceneKit } from '@london-life/game/scene/kit';
import type { RideSetting } from './path';

// The world a ride passes through, built forty metres at a time ahead of the player and dropped behind:
// a London street (terraces with shops, white stucco, glass offices, plane trees, lamp posts, red phone and post
// boxes, bus stops, zebra crossings with their beacons), the DLR's viaduct among Docklands towers over the
// docks, or the Tube's tunnel with its platforms. The way runs along +z; traffic keeps left (the player's lane is
// on +x, the screen's left from behind).

export const CHUNK = 40;
export const LANE = 1.75;
const ROAD = 3.5; // half the road's width
const PAVEMENT = 3.2;
const FRONT = ROAD + PAVEMENT; // where the buildings start

export type ChunkPlan = {
  setting: RideSetting;
  index: number;
  random: () => number;
  night: boolean;
  // "Now passing" signs and the place itself, where they fall in this chunk (metres from the chunk's start)
  signs: { at: number; text: string }[];
  arrival: { at: number; name: string; icon: string } | null;
  // a building's walls in docklands colours, where the way passes Canary Wharf and the like
  modern: boolean;
};

const pick = <V>(random: () => number, list: readonly V[]) => list[Math.floor(random() * list.length) % list.length]!;
const BRICK = ['#8b4a36', '#9c5a40', '#7a3e2e', '#b79a6b', '#a8643f', '#c4a777'];
const STUCCO = ['#efe9dc', '#f3efe6', '#e9e2d2'];
const GLASS = ['#9fb4c5', '#8aa2b5', '#b3c4d1', '#7f97ab', '#c9d3d8'];
const SHOPS = [
  { name: 'Café', colour: '#2f6b4f' },
  { name: 'Corner Shop', colour: '#1f4e8c' },
  { name: 'Fish & Chips', colour: '#1d5fa5' },
  { name: 'Barber', colour: '#8c2b2b' },
  { name: 'Pharmacy', colour: '#1f7a55' },
  { name: 'Bakery', colour: '#a5652a' },
  { name: 'Launderette', colour: '#4a5bb0' },
  { name: 'The Crown', colour: '#2b2b2b' },
  { name: 'Florist', colour: '#7a3f78' },
  { name: 'Books', colour: '#5a3a24' },
] as const;
const AWNINGS = ['#2f6b4f', '#8c2b2b', '#1f4e8c', '#d8a32a', '#3b3b3b'];

// windows on a building's street side: rows and columns of glass, lit at night here and there
function windows(
  kit: SceneKit,
  parent: T.Object3D,
  side: number,
  x: number,
  z: number,
  width: number,
  from: number,
  floors: number,
  random: () => number,
  night: boolean,
  frame: string,
) {
  const across = Math.max(1, Math.floor(width / 2.4));
  for (let floor = 0; floor < floors; floor += 1)
    for (let col = 0; col < across; col += 1) {
      const wz = z - width / 2 + (col + 0.5) * (width / across);
      const wy = from + floor * 3.2 + 1.5;
      kit.box(0.08, 1.7, 1.15, frame, x - side * 0.02, wy, wz, parent);
      if (night && random() < 0.55) kit.lit(0.06, 1.45, 0.9, '#ffd98a', x - side * 0.06, wy, wz, parent, 0.9);
      else kit.box(0.06, 1.45, 0.9, '#2c3a46', x - side * 0.06, wy, wz, parent);
    }
}

// one building facing the road: x is its street face (side +1 is the left of the road, -1 the right)
function building(kit: SceneKit, parent: T.Object3D, side: number, z: number, width: number, plan: ChunkPlan) {
  const { random, night } = plan;
  const face = side * FRONT;
  const depth = 12;
  const cx = face + side * (depth / 2);
  const kind = plan.modern
    ? random() < 0.7
      ? 'tower'
      : 'terrace'
    : random() < 0.15
      ? 'tower'
      : random() < 0.3
        ? 'stucco'
        : 'terrace';
  if (kind === 'tower') {
    const height = 24 + random() * 46;
    const glass = pick(random, GLASS);
    kit.box(depth, height, width - 0.4, glass, cx, height / 2, z, parent);
    // the floors as bands, lit at night
    for (let y = 3.5; y < height; y += 3.6)
      if (night)
        kit.lit(0.1, 0.5, width - 0.8, '#ffe2a0', face - side * 0.02, y, z, parent, random() < 0.5 ? 0.8 : 0.2);
      else kit.box(0.1, 0.25, width - 0.6, '#c9d6df', face - side * 0.02, y, z, parent);
    if (random() < 0.4) kit.lit(0.2, 0.4, 0.4, '#e0302a', cx, height + 0.3, z, parent, 1.4);
    return;
  }
  const floors = 3 + Math.floor(random() * 2);
  const height = floors * 3.2 + 3.6;
  const wall = kind === 'stucco' ? pick(random, STUCCO) : pick(random, BRICK);
  kit.box(depth, height, width - 0.15, wall, cx, height / 2, z, parent);
  kit.box(depth + 0.2, 0.35, width, kind === 'stucco' ? '#ffffff' : '#d9d2c3', cx, height + 0.1, z, parent);
  windows(kit, parent, side, face, z, width, 3.6, floors, random, night, kind === 'stucco' ? '#ffffff' : '#f2efe8');
  // the ground floor: a shop with its name over the window and a striped awning, or a front door with railings
  if (kind === 'terrace' && random() < 0.75) {
    const shop = pick(random, SHOPS);
    kit.box(0.12, 0.7, width - 0.8, shop.colour, face - side * 0.03, 3.15, z, parent);
    kit.sign(
      shop.name,
      face - side * 0.12,
      3.15,
      z,
      parent,
      Math.min(3.2, width - 1.2),
      '#ffffff',
      shop.colour,
      side > 0 ? -Math.PI / 2 : Math.PI / 2,
    );
    if (night) kit.lit(0.08, 2.1, width - 1.2, '#ffe6b0', face - side * 0.05, 1.5, z, parent, 0.8);
    else kit.box(0.08, 2.1, width - 1.2, '#3c4d5a', face - side * 0.05, 1.5, z, parent);
    const awning = kit.box(1.2, 0.08, width - 1, pick(random, AWNINGS), face - side * 0.6, 2.65, z, parent);
    awning.rotation.z = side * 0.25;
  } else {
    kit.box(
      0.1,
      2.3,
      1.1,
      kind === 'stucco' ? '#1d1d1d' : pick(random, ['#1d3a6b', '#6b1d1d', '#1d4d2f', '#1d1d1d']),
      face - side * 0.04,
      1.2,
      z,
      parent,
    );
    for (let r = -width / 2 + 0.4; r < width / 2; r += 0.3)
      kit.box(0.04, 1.0, 0.04, '#141414', face - side * 0.6, 0.6, z + r, parent);
  }
}

// the things on a pavement, every few metres
function furniture(kit: SceneKit, parent: T.Object3D, side: number, z: number, plan: ChunkPlan) {
  const { random, night } = plan;
  const kerb = side * (ROAD + 0.6);
  const roll = random();
  if (roll < 0.34) {
    // a plane tree
    kit.cylinder(0.18, 3.2, '#6b5a45', kerb, 1.6, z, parent, 0.14, 8);
    for (const [dx, dy, dz, r] of [
      [0, 4.4, 0, 1.6],
      [0.6, 3.9, 0.7, 1.2],
      [-0.5, 4.0, -0.6, 1.25],
    ] as const)
      kit.sphere(r, '#4f7a3a', kerb + dx, dy, z + dz, parent);
  } else if (roll < 0.6) {
    // a lamp post, lit at night
    kit.cylinder(0.08, 6, '#22262b', kerb, 3, z, parent, 0.07, 8);
    kit.box(0.16, 0.12, 1.1, '#22262b', kerb - side * 0.5, 6, z, parent);
    const lamp = kit.lit(
      0.36,
      0.14,
      0.5,
      night ? '#ffd27a' : '#f4ead2',
      kerb - side * 1.0,
      5.92,
      z,
      parent,
      night ? 2 : 0.3,
    );
    lamp.rotation.y = 0;
  } else if (roll < 0.7) {
    // a red phone box
    const x = side * (FRONT - 0.9);
    kit.box(1.0, 2.5, 1.0, '#c0231f', x, 1.25, z, parent);
    kit.box(1.04, 1.4, 0.8, '#2c3a46', x, 1.35, z, parent);
    kit.box(1.08, 0.18, 1.08, '#c0231f', x, 2.55, z, parent);
  } else if (roll < 0.8) {
    // a red post box
    kit.cylinder(0.28, 1.25, '#c0231f', kerb - side * 0.3, 0.62, z, parent, 0.28, 12);
    kit.sphere(0.29, '#c0231f', kerb - side * 0.3, 1.25, z, parent);
    kit.box(0.05, 0.06, 0.24, '#141414', kerb - side * 0.3 - side * 0.28, 1.0, z, parent);
  } else if (roll < 0.9) {
    // a bus stop
    const x = kerb - side * 0.9;
    kit.box(0.08, 2.4, 3.2, '#b8c7d1', x - side * 0.6, 1.2, z, parent);
    kit.box(1.4, 0.08, 3.4, '#2e3a44', x, 2.45, z, parent);
    kit.lit(0.08, 1.4, 0.9, '#f4f4f4', x + side * 0.6, 1.4, z + 1.4, parent, night ? 1 : 0.3);
    kit.box(0.5, 0.06, 2.2, '#3b4650', x - side * 0.3, 0.55, z, parent);
  } else {
    // a bin and a bench
    kit.cylinder(0.25, 0.9, '#1d2b23', kerb - side * 0.4, 0.45, z, parent, 0.25, 10);
    kit.box(0.5, 0.45, 1.6, '#5a4632', side * (FRONT - 0.6), 0.45, z + 1.2, parent);
  }
}

// a "Now passing" sign on a post at the edge of the pavement on the left, facing the traffic
function passing(kit: SceneKit, parent: T.Object3D, z: number, text: string, high = 0) {
  const x = ROAD + PAVEMENT / 2;
  kit.cylinder(0.07, 3.0 + high, '#2b3036', ROAD + 0.5, (3.0 + high) / 2, z, parent, 0.07, 8);
  kit.box(2.9, 0.8, 0.08, '#ffffff', x, 3.2 + high, z, parent);
  kit.sign(`Now passing: ${text}`, x, 3.2 + high, z - 0.06, parent, 2.8, '#111111', '#ffffff', Math.PI);
  kit.box(2.9, 0.07, 0.09, '#d4202a', x, 2.83 + high, z, parent);
}

// the place itself: its front on the left with its name and icon, where the ride stops
function placeFront(kit: SceneKit, parent: T.Object3D, z: number, name: string, icon: string, plan: ChunkPlan) {
  const face = FRONT;
  kit.box(14, 12, 16, '#e7e2d6', face + 7, 6, z, parent);
  kit.box(0.2, 1.4, 12, '#1f3a5f', face - 0.05, 9.6, z, parent);
  kit.sign(`${icon} ${name}`, face - 0.18, 9.6, z, parent, 10, '#ffffff', '#1f3a5f', -Math.PI / 2);
  kit.lit(0.1, 3.2, 6, plan.night ? '#ffe6b0' : '#cfe3ee', face - 0.06, 1.8, z, parent, plan.night ? 1 : 0.4);
  // a welcome mat of light on the pavement
  kit.box(PAVEMENT - 0.4, 0.04, 6, '#d9c27a', ROAD + PAVEMENT / 2, 0.15, z, parent);
}

function street(kit: SceneKit, chunk: T.Group, plan: ChunkPlan) {
  const { random } = plan;
  // the road, its kerbs, the double yellow lines and the dashed white middle line, the pavements
  kit.box(ROAD * 2, 0.1, CHUNK, '#3a3d42', 0, 0, CHUNK / 2, chunk);
  for (const side of [-1, 1]) {
    kit.box(0.25, 0.18, CHUNK, '#b9b6ae', side * (ROAD + 0.12), 0.09, CHUNK / 2, chunk);
    kit.box(PAVEMENT, 0.16, CHUNK, '#c9c5bc', side * (ROAD + PAVEMENT / 2), 0.08, CHUNK / 2, chunk);
    for (const dx of [0.25, 0.42]) kit.box(0.1, 0.02, CHUNK, '#e8c23a', side * (ROAD - dx), 0.06, CHUNK / 2, chunk);
  }
  for (let z = 1; z < CHUNK; z += 6) kit.box(0.14, 0.02, 3, '#f2f2f2', 0, 0.06, z + 1.5, chunk);
  // a zebra crossing now and then, with its flashing beacons
  if (random() < 0.3) {
    const z = 6 + random() * (CHUNK - 12);
    for (let x = -ROAD + 0.3; x < ROAD; x += 1.0) kit.box(0.6, 0.02, 2.6, '#f4f4f4', x + 0.3, 0.065, z, chunk);
    for (const side of [-1, 1]) {
      kit.cylinder(0.07, 2.6, '#f4f4f4', side * (ROAD + 0.5), 1.3, z - 1.6, chunk, 0.07, 8);
      kit.lit(0.32, 0.32, 0.32, '#ff9d2a', side * (ROAD + 0.5), 2.75, z - 1.6, chunk, 1.6);
    }
  }
  // buildings shoulder to shoulder on both sides; things along the pavements
  for (const side of [-1, 1]) {
    let z = 0;
    while (z < CHUNK) {
      const width = 6 + random() * 6;
      const end = Math.min(CHUNK, z + width);
      const arrival = side > 0 && plan.arrival && plan.arrival.at >= z - 8 && plan.arrival.at < end + 8;
      if (!arrival) building(kit, chunk, side, (z + end) / 2, end - z, plan);
      z = end;
    }
    for (let z = 3 + random() * 6; z < CHUNK; z += 9 + random() * 8) furniture(kit, chunk, side, z, plan);
  }
  for (const sign of plan.signs) passing(kit, chunk, sign.at, sign.text);
  if (plan.arrival) placeFront(kit, chunk, plan.arrival.at, plan.arrival.name, plan.arrival.icon, plan);
}

// the DLR: a concrete deck seven metres up on pillars, rails, glass towers on both sides, the docks below
const DECK = 7;
function viaduct(kit: SceneKit, chunk: T.Group, plan: ChunkPlan) {
  const { random, night } = plan;
  kit.box(5.4, 0.7, CHUNK, '#b9b7b0', 0, DECK - 0.35, CHUNK / 2, chunk);
  for (const x of [-0.75, 0.75]) kit.box(0.12, 0.14, CHUNK, '#6f747a', x, DECK + 0.07, CHUNK / 2, chunk);
  for (const side of [-1, 1]) kit.box(0.15, 1.0, CHUNK, '#d5d3cc', side * 2.65, DECK + 0.5, CHUNK / 2, chunk);
  for (let z = 4; z < CHUNK; z += 20) kit.box(1.4, DECK - 0.7, 1.4, '#a9a7a0', 0, (DECK - 0.7) / 2, z, chunk);
  // below: water in the docks, or a street
  const water = random() < 0.45;
  kit.box(60, 0.1, CHUNK, water ? '#3d6f8f' : '#3a3d42', 0, -0.05, CHUNK / 2, chunk);
  if (water) for (const side of [-1, 1]) kit.box(4, 0.8, CHUNK, '#9c9a92', side * 13, 0.4, CHUNK / 2, chunk);
  for (const side of [-1, 1]) {
    let z = 0;
    while (z < CHUNK) {
      const width = 12 + random() * 10;
      const end = Math.min(CHUNK, z + width);
      // the docks keep the towers back from the line: a few tall ones, lower blocks between
      const tall = random() < 0.4;
      const height = tall ? 45 + random() * 60 : 12 + random() * 18;
      const x = side * (water ? 26 + random() * 12 : 18 + random() * 12);
      kit.box(14, height, end - z - 1, pick(random, GLASS), x, height / 2, (z + end) / 2, chunk);
      for (let y = 4; y < height; y += 4)
        if (night)
          kit.lit(14.1, 0.5, end - z - 1.1, '#ffe2a0', x, y, (z + end) / 2, chunk, random() < 0.5 ? 0.7 : 0.15);
        else kit.box(14.1, 0.2, end - z - 1.1, '#c9d6df', x, y, (z + end) / 2, chunk);
      if (height > 70) kit.lit(0.6, 0.6, 0.6, '#e0302a', x, height + 0.4, (z + end) / 2, chunk, 1.5);
      z = end;
    }
  }
  for (const sign of plan.signs) passing(kit, chunk, sign.at, sign.text, DECK - 0.6);
  if (plan.arrival) platform(kit, chunk, plan.arrival.at, plan.arrival.name, DECK, '#c9c5bc', night);
}

// a station platform on the left with the place's name on boards
function platform(
  kit: SceneKit,
  parent: T.Object3D,
  z: number,
  name: string,
  level: number,
  floor: string,
  night: boolean,
) {
  kit.box(3.4, 1.0, 30, floor, 3.2, level - 0.5, z, parent);
  kit.box(0.2, 0.02, 30, '#e8c23a', 1.6, level + 0.01, z, parent);
  kit.box(3.6, 0.2, 30, '#41505c', 3.2, level + 3.4, z, parent);
  for (const dz of [-8, 0, 8]) {
    kit.box(0.08, 0.7, 3.2, '#16336b', 4.6, level + 2.2, z + dz, parent);
    kit.sign(name, 4.55, level + 2.2, z + dz, parent, 3.0, '#ffffff', '#16336b', -Math.PI / 2);
  }
  kit.lit(3.2, 0.06, 28, night ? '#fff1cf' : '#f6f2e6', 3.2, level + 3.28, z, parent, 0.8);
}

// the Tube: a round tunnel with cables and lamps along it, and now and then a station (a tiled hall with the
// platform on the left)
function tunnel(kit: SceneKit, chunk: T.Group, plan: ChunkPlan) {
  const { random } = plan;
  const station =
    plan.arrival ?? (random() < 0.25 && plan.signs[0] ? { at: CHUNK / 2, name: plan.signs[0].text } : null);
  kit.box(2.4, 0.1, CHUNK, '#2a2c2f', 0, -0.4, CHUNK / 2, chunk);
  for (const x of [-0.72, 0.72]) kit.box(0.1, 0.12, CHUNK, '#7b8086', x, -0.3, CHUNK / 2, chunk);
  if (station) {
    kit.box(0.3, 5.2, CHUNK, '#e9dfc4', 5.0, 2.2, CHUNK / 2, chunk);
    kit.box(0.3, 5.2, CHUNK, '#34383d', -2.0, 2.2, CHUNK / 2, chunk);
    kit.box(7.4, 0.3, CHUNK, '#d8cdb0', 1.5, 4.8, CHUNK / 2, chunk);
    for (let z = 2; z < CHUNK; z += 4) kit.box(0.04, 0.12, 3.6, '#2a5f8f', 4.84, 3.2, z + 1.8, chunk);
    platform(kit, chunk, station.at, station.name, 0.6, '#9a8f7a', true);
    kit.box(1.6, 0.03, 30, '#f2f2f2', 2.1, 0.62, station.at, chunk);
    return;
  }
  const wall = new T.MeshStandardMaterial({ color: '#3b3f45', roughness: 0.95, side: T.BackSide, flatShading: true });
  kit.own(wall);
  const tube = new T.Mesh(
    kit.geo('tunnel', () => new T.CylinderGeometry(2.7, 2.7, CHUNK, 18, 1, true)),
    wall,
  );
  tube.position.set(0, 1.7, CHUNK / 2);
  tube.rotation.x = Math.PI / 2;
  chunk.add(tube);
  for (const side of [-1, 1])
    for (const y of [0.9, 1.1, 1.3]) kit.box(0.06, 0.06, CHUNK, '#1b1d20', side * 2.35, y, CHUNK / 2, chunk);
  for (let z = 2; z < CHUNK; z += 8) kit.lit(0.3, 0.15, 0.6, '#fff1c9', -2.2, 2.4, z, chunk, 1.6);
}

// the main line out of King's Cross: a Victorian brick viaduct six metres up with two tracks (ours on the left),
// the overhead wires on their masts, and below it London's back streets: terraces with their chimneys and long
// back gardens, now and then a tower block, a gasholder's iron frame or a church spire; the stations on the way
// (their names on the platform's boards) and the stop itself
export const RAIL_DECK = 6;
// the gasholders' iron frames: one wire material per scene kit, freed with it
const gasFrames = new WeakMap<SceneKit, T.Material>();
const gasFrame = (kit: SceneKit) => {
  let material = gasFrames.get(kit);
  if (!material) {
    material = new T.MeshBasicMaterial({ color: '#5f6f6a', wireframe: true });
    kit.own(material);
    gasFrames.set(kit, material);
  }
  return material;
};
export const RAIL_TRACK = 2;
function railway(kit: SceneKit, chunk: T.Group, plan: ChunkPlan) {
  const { random, night } = plan;
  const deck = RAIL_DECK;
  // the viaduct: the deck, its parapets and the brick arches below
  kit.box(9, 0.6, CHUNK, '#6f6a62', 0, deck - 0.3, CHUNK / 2, chunk);
  for (const side of [-1, 1]) kit.box(0.4, 0.9, CHUNK, '#8b4a36', side * 4.5, deck + 0.45, CHUNK / 2, chunk);
  for (let z = 0; z < CHUNK; z += 8) kit.box(8.6, deck - 0.6, 1.4, '#8b4a36', 0, (deck - 0.6) / 2, z + 0.7, chunk);
  for (let z = 4; z < CHUNK; z += 8) kit.box(8.6, 1.2, 6.6, '#7a3e2e', 0, deck - 1.2, z + 0.7, chunk);
  // the two tracks: ballast, sleepers now and then, rails
  for (const x of [-RAIL_TRACK, RAIL_TRACK]) {
    kit.box(2.8, 0.2, CHUNK, '#8f877a', x, deck + 0.1, CHUNK / 2, chunk);
    for (const dx of [-0.72, 0.72]) kit.box(0.08, 0.14, CHUNK, '#7b8086', x + dx, deck + 0.27, CHUNK / 2, chunk);
    for (let z = 1; z < CHUNK; z += 2.5) kit.box(2.3, 0.08, 0.25, '#5a4b3c', x, deck + 0.21, z, chunk);
  }
  // the overhead wires: a mast each side every twenty metres, the beam across, the wires along
  for (let z = 5; z < CHUNK; z += 20) {
    for (const side of [-1, 1]) kit.box(0.25, 6.4, 0.25, '#4b545c', side * 4.1, deck + 3.2, z, chunk);
    kit.box(8.4, 0.22, 0.22, '#4b545c', 0, deck + 6.2, z, chunk);
  }
  for (const x of [-RAIL_TRACK, RAIL_TRACK]) kit.box(0.04, 0.04, CHUNK, '#2a2d31', x, deck + 5.4, CHUNK / 2, chunk);
  // a station on the way: platforms both sides with its name on the boards
  const station = plan.signs[0];
  if (station) {
    for (const side of [-1, 1]) {
      kit.box(3.4, 1.0, 34, '#cfccc4', side * 5.4, deck + 0.5, station.at, chunk);
      kit.box(0.2, 0.02, 34, '#e8c23a', side * 3.8, deck + 1.01, station.at, chunk);
      kit.box(3.6, 0.2, 30, '#3d4650', side * 5.6, deck + 4.2, station.at, chunk);
      for (const dz of [-10, 0, 10]) {
        kit.box(0.1, 3.2, 0.1, '#3d4650', side * 6.6, deck + 2.6, station.at + dz, chunk);
        kit.box(0.08, 0.8, 3.4, '#1d3c8f', side * 6.55, deck + 2.6, station.at + dz, chunk);
        kit.sign(
          station.text,
          side * 6.5,
          deck + 2.6,
          station.at + dz,
          chunk,
          3.2,
          '#ffffff',
          '#1d3c8f',
          -side * (Math.PI / 2),
        );
      }
      kit.lit(3.2, 0.06, 28, night ? '#fff1cf' : '#f6f2e6', side * 5.6, deck + 4.08, station.at, chunk, 0.8);
    }
  }
  if (plan.arrival) platform(kit, chunk, plan.arrival.at, plan.arrival.name, deck + 1, '#cfccc4', night);
  // below and beside: back streets of terraces, their gardens towards the line, chimneys on the roofs
  for (const side of [-1, 1]) {
    kit.box(40, 0.1, CHUNK, '#6f8f5a', side * 24, -0.05, CHUNK / 2, chunk);
    let z = 0;
    while (z < CHUNK) {
      const width = 5 + random() * 3;
      const end = Math.min(CHUNK, z + width);
      const middle = (z + end) / 2;
      const roll = random();
      if (roll < 0.08) {
        // a tower block, its windows lit at night
        const height = 30 + random() * 25;
        kit.box(12, height, end - z, '#cfd2cf', side * 26, height / 2, middle, chunk);
        for (let y = 4; y < height; y += 3)
          if (night)
            kit.lit(12.1, 0.6, end - z - 0.4, '#ffe2a0', side * 26, y, middle, chunk, random() < 0.5 ? 0.7 : 0.15);
          else kit.box(12.1, 0.25, end - z - 0.3, '#9fb4c5', side * 26, y, middle, chunk);
      } else if (roll < 0.11) {
        // a gasholder's iron frame
        const frame = new T.Mesh(
          kit.geo('gasframe', () => new T.CylinderGeometry(7, 7, 14, 12, 3, true)),
          gasFrame(kit),
        );
        frame.position.set(side * 28, 7, middle);
        chunk.add(frame);
      } else if (roll < 0.13) {
        // a church with its spire
        kit.box(7, 8, end - z, '#b8ad97', side * 20, 4, middle, chunk);
        kit.box(2.4, 14, 2.4, '#b8ad97', side * 20, 7, z + 1.4, chunk);
        kit.cone(1.7, 8, '#6d6a62', side * 20, 18, z + 1.4, chunk, 4);
      } else {
        // a terraced house: brick front, slate roof, a chimney, the back garden with its fence
        const brick = pick(random, BRICK);
        const h = 6 + random() * 2.5;
        kit.box(7, h, end - z - 0.1, brick, side * 16, h / 2, middle, chunk);
        kit.box(7.4, 0.5, end - z, '#55606a', side * 16, h + 0.25, middle, chunk);
        kit.box(0.9, 1.6, 0.9, brick, side * 16, h + 1.1, middle, chunk);
        for (let y = 1.6; y < h - 1; y += 2.6)
          if (night) kit.lit(0.1, 1.1, 1.0, '#ffe2a0', side * 12.45, y, middle, chunk, random() < 0.5 ? 0.9 : 0.1);
          else kit.box(0.1, 1.1, 1.0, '#2f3a45', side * 12.45, y, middle, chunk);
        kit.box(0.1, 1.2, end - z, '#7a5a3c', side * 8.3, 0.6, end - 0.05, chunk);
      }
      z = end;
    }
  }
}

// one chunk of the way, from its start (z = index × CHUNK)
export function buildChunk(kit: SceneKit, plan: ChunkPlan) {
  const chunk = new T.Group();
  chunk.position.z = plan.index * CHUNK;
  if (plan.setting === 'street') street(kit, chunk, plan);
  else if (plan.setting === 'viaduct') viaduct(kit, chunk, plan);
  else if (plan.setting === 'railway') railway(kit, chunk, plan);
  else tunnel(kit, chunk, plan);
  return chunk;
}
export const RIDE_DECK = { street: 0, viaduct: DECK, tunnel: 0, railway: RAIL_DECK } as const;
